import { ElementState, Preview } from "@creatomate/preview";

import { ElementSource, Subtitle, SubtitleSection } from "chq";

import { errorService } from "app/utils";
import { timeToNumber } from "components/stories/video-studio/utils";
import isSubtitleText from "./isSubtitleText";
import hasBurnSubtitlesOn from "./hasBurnSubtitlesOn";
import createSubtitleElement from "./createSubtitleElement";
import addSpacesToText from "./addSpacesToText";
import parseTimes from "./parseTimes";
import loadJsonSubtitles from "./loadJsonSubtitles";

interface AdjustSubtitleTimeResult {
  keepSubtitle: boolean;
  elementStartTime: number;
  elementEndTime: number;
}

/*
adjust subtitles according to trimmins and position of the media, this only applies for new
generated subtitles because the ones that were previosuly created should be adjusted
by this moment (Edit an existing video)
*/
const adjustSubtitleTime = (
  startTime: number,
  endTime: number,
  isNewSubtitle: boolean,
  isMontage: boolean,
  originalTrimStart: number,
  isTheOriginal: boolean,
  originalsMedia?: ElementState[]
): AdjustSubtitleTimeResult => {
  let keepSubtitle = true;
  let elementStartTime = startTime;
  let elementEndTime = endTime;

  if (!isTheOriginal || !isNewSubtitle) return { keepSubtitle, elementStartTime, elementEndTime };
  if (!originalsMedia) return { keepSubtitle, elementStartTime, elementEndTime };

  const validRanges = originalsMedia.map(media => {
    const trimStartValue = timeToNumber(media.source.trim_start) - originalTrimStart;
    return {
      localTime: media.localTime,
      duration: media.duration,
      startTime: trimStartValue,
      endTime: media.duration + trimStartValue + media.localTime
    };
  });

  const validRange = validRanges.find(
    range => (range.startTime < startTime && range.endTime > endTime)
  );

  if (!validRange) return { keepSubtitle: false, elementStartTime, elementEndTime };

  if ((validRange.startTime > endTime) || (validRange.endTime < startTime)) {
    keepSubtitle = false;
  } else if (!isMontage) {
    elementStartTime = Math.max(
      validRange.localTime, (startTime - validRange.startTime) + validRange.localTime
    );
    elementEndTime = Math.min(
      validRange.localTime + validRange.duration,
      (endTime - validRange.startTime) + validRange.localTime
    );
  }

  return { keepSubtitle, elementStartTime, elementEndTime };
};

const parseSubtitleSections = (
  subtitleText: string,
  isNewSubtitle: boolean,
  isMontage: boolean,
  originalTrimStart: number,
  isTheOriginal: boolean,
  originalsMedia?: ElementState[]
): SubtitleSection[] => {
  const sections: SubtitleSection[] = [];
  const vttSections = subtitleText.split("\n\n");

  vttSections.forEach(section => {
    const parsedLine = section.trim().split("\n");

    if (parsedLine.length > 2) {
      const [index, time, text] = parsedLine;
      const { startTime, endTime } = parseTimes(time);
      const { keepSubtitle, elementStartTime, elementEndTime } = adjustSubtitleTime(
        startTime,
        endTime,
        isNewSubtitle,
        isMontage,
        originalTrimStart,
        isTheOriginal,
        originalsMedia
      );

      if (keepSubtitle) {
        sections.push({
          text,
          startTime: elementStartTime,
          endTime: elementEndTime,
          index: parseInt(index, 10),
          originalStartTime: elementStartTime,
          originalText: text,
          originalEndTime: elementEndTime,
          creatomateId: undefined
        });
      }
    }
  });

  return sections;
};

const loadSubtitleSections = async (
  subtitles: Subtitle[],
  subtitle: Subtitle,
  active: boolean,
  preview: Preview,
  organization: string,
  originalTrimStart: number,
  handleUpdateData: (data: ElementSource) => void,
  language?: string,
  burnSubtitles = true,
  isNewSubtitle = false,
  isTheOriginal = false
): Promise<Subtitle> => {
  let isMontage = false;
  const elements = preview.state?.elements || [];
  const source = preview.getSource();
  const subtitlesElements = elements.filter(element => isSubtitleText(element.source.name));
  const originalsMedia = elements.filter(element => element.source.name?.startsWith("media-"));
  const videoIsLandscape = source.width === 1280;
  const namesModifications: ElementSource = {};
  const newElements: ElementSource[] = [];
  const newTrack = Math.max(...elements.map(element => element.track)) + 1;

  const isOrientalFont = language?.includes("zh") || language === "jp" || language === "ja" || language === "ko";
  const landscapeInterval = isOrientalFont ? 28 : 50;
  const portraitInterval = isOrientalFont ? 15 : 28;
  const breakInterval = videoIsLandscape ? landscapeInterval : portraitInterval;

  // it means we are in a montage because we do not have media- elements
  if (originalsMedia.length === 0) {
    isMontage = true;
    originalsMedia.push(...elements.filter(element => element.source.name?.includes("'s video")));
  }

  // it means subtitles are loaded from the draft json version so
  // we need to match that with the vtt file
  if (active && subtitlesElements.length > 0) {
    return loadJsonSubtitles(subtitles, subtitle, active, subtitlesElements, handleUpdateData);
  }

  const sections = parseSubtitleSections(
    subtitle.text || "",
    isNewSubtitle,
    isMontage,
    originalTrimStart,
    isTheOriginal,
    originalsMedia
  );

  if (active) {
    const updatedSections = sections.map(section => {
      if ((burnSubtitles || subtitle.draft) && subtitlesElements.length) {
        const element = subtitlesElements.find(element => {
          // this is the most accurate way that we have found to identify existing subtitles
          const match = element.source.name.match(/sub-(\d{1,4})-/);
          if (match) return parseInt(match[1], 10) === section.index;

          if (Math.round(element.globalTime) === Math.round(section.startTime)) {
            namesModifications[element.source.id] = `sub-${section.index}-${element.source.id}`;
            return true;
          }

          return false;
        });

        if (element) {
          return {
            ...section,
            creatomateId: element.source.id,
            track: element.track
          };
        }

        errorService.error(`Subtitles mismatch for ${organization} - ${subtitle.id} - ${section.startTime}`);
        return section;
      }

      const subtitleElement = createSubtitleElement(
        addSpacesToText(section.text, breakInterval),
        section.index,
        section.startTime,
        section.endTime,
        newTrack,
        organization,
        undefined,
        hasBurnSubtitlesOn(organization) && isNewSubtitle,
        language
      );
      newElements.push(subtitleElement);
      return {
        ...section,
        creatomateId: subtitleElement.id,
        track: newTrack
      };
    });

    if (newElements.length > 0) {
      await preview.setSource({ ...source, elements: [...source.elements, ...newElements] }, false);
    }

    // This code is to sync old subtitles with the new format sub-INDEX-id
    if (burnSubtitles && Object.keys(namesModifications).length > 0) {
      const updatedElements = source.elements.map((element: ElementSource) => ({
        ...element,
        name: namesModifications[element.id] || element.name
      }));
      await preview.setSource({ ...source, elements: updatedElements }, false);
    }

    handleUpdateData({
      subtitles: subtitles.map(existingSub => {
        if (existingSub.id !== subtitle.id) return existingSub;
        return { ...subtitle, active, sections: updatedSections, deleted: undefined };
      })
    });
    return { ...subtitle, active, sections: updatedSections };
  }

  handleUpdateData({
    subtitles: subtitles.map(existingSub => {
      if (existingSub.id !== subtitle.id) return existingSub;
      return { ...subtitle, active, sections, deleted: undefined };
    })
  });
  return { ...subtitle, active, sections };
};

export default loadSubtitleSections;
